import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme, CircularProgress } from '@mui/material';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';

import Iconify from '../../Components/iconify';
import { usePopover, CustomPopover } from '../../Components/custom-popover';
import { UserIcon, CalendarIcon, NotificationIcon, SuperAdminIcon, DocIcon } from '../../Components/Global/Icons';
import { NoticeIcon, FAQIcon, ResourcesIcon } from '../../Components/Global/Icons';

// ----------------------------------------------------------------------

function getFirstUrlPart(url) {
  // Split the URL by '/' and filter out empty strings
  const parts = url.split('/').filter((part) => part !== '');
  // Return the first part with a leading slash
  return parts.length > 0 ? `/${parts[0]}` : '';
}

export function WorkspacesPopover({ data = [], sx, ...other }) {
  const popover = usePopover();
  const theme = useTheme();
  const mediaQuery = 'sm';

  const location = useLocation();
  const selectedHub = getFirstUrlPart(location.pathname);

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const newWorkspace =
      selectedHub === '/admin'
        ? { name: 'Admin panel', icon: <UserIcon /> }
        : selectedHub === '/calendar'
        ? { name: 'Calendar', icon: <CalendarIcon /> }
        : selectedHub === '/notifications'
        ? { name: 'Notifications', icon: <NotificationIcon /> }
        : selectedHub === '/superadmin'
        ? { name: 'Q-Admin', icon: <SuperAdminIcon /> }
        : selectedHub === '/profile'
        ? { name: 'Profile', icon: <UserIcon /> }
        : selectedHub === '/data'
        ? { name: 'Data sets', icon: <DocIcon /> }
        : selectedHub === '/editor'
        ? { name: 'Doc Hub', icon: <DocIcon /> }
        : selectedHub === '/noticeBoard'
        ? { name: 'Notice board', icon: <NoticeIcon /> }
        : selectedHub === '/help'
        ? { name: 'Help', icon: <FAQIcon /> }
        : selectedHub === '/resources'
        ? { name: 'Resources', icon: <ResourcesIcon /> }
        : data.find((hub) => selectedHub.startsWith(hub.path)) || data[0];

    setWorkspace(newWorkspace);
    setLoading(false);
  }, [selectedHub, data]);

  const handleChangeWorkspace = useCallback(
    (newValue) => {
      setWorkspace(newValue);
      popover.onClose();
    },
    [popover],
  );

  return (
    <>
      <Button
        disableRipple
        variant={'soft'}
        onClick={popover.onOpen}
        sx={{
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          ...sx,
        }}
        {...other}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {workspace?.logo && (
              <Box
                component="img"
                alt={workspace?.name}
                src={workspace?.logo}
                sx={{ width: 24, height: 24, borderRadius: '50%' }}
              />
            )}
            {workspace?.icon && workspace?.icon}
            <Box
              component="span"
              sx={{
                typography: 'subtitle2',
                display: { xs: 'none', [mediaQuery]: 'inline-flex' },
              }}
            >
              {workspace?.name || 'Choose a Hub'}
            </Box>
          </>
        )}
        <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
      </Button>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        className="scroll-bar"
        slotProps={{ arrow: { placement: 'top-center' } }}
      >
        <MenuList sx={{ width: 240, maxHeight: '90vh', overflow: 'auto' }}>
          {data.map((option) => (
            <Link
              to={option.path}
              key={option.id}
              style={{ textDecoration: 'none', color: theme.palette.text.primary }}
            >
              <MenuItem
                selected={option.path === location.pathname}
                onClick={() => handleChangeWorkspace(option)}
                sx={{ height: 48 }}
              >
                {option.logo && <Avatar alt={option.name} src={option.logo} sx={{ width: 24, height: 24 }} />}
                {option.icon && option.icon}

                <Box component="span" sx={{ flexGrow: 1 }}>
                  {option.name}
                </Box>
              </MenuItem>
            </Link>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
